/**
 * クラス名：係数パラメータ
 * 説明：係数情報詳細画面
 * 作成者：ケイ
 * 作成日：2023/05/01
 * バージョン：1.0
 */
import React, { Component, useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { Modal, Row, Col, Divider, Button, Radio, Image, Form, DatePicker, Input, Space, Popover } from 'antd';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table } from 'react-bootstrap';
import { WithRouter } from '../../../../components/WithRouter';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseCircleFilled, CloseOutlined, PlusCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  confirmDiscardContent,
  confirmUpdateContent,
  confirmModalTitle,
  commonModal,
  commonFooter,
  error,
  commonResponsiveConfirmModal,
} from '../../../common/CommonModal';
import {
  today,
  TextArea,
  formValidatorMode,
  handleKeyPress,
  isDigit,
  isDecimal,
  toDecimal,
  defaultQuantiyData,
  getInputBorderStyle,
  isDigitZenkaku,
  toHalfWidth,
  regexExp,
  deleteListColData,
  updateListColData,
  sortListData,
} from '../../../common/Common';
import checked from '../../../../assets/images/checked.png';
import { ErrorMessage } from '../../../common/Message';
import { getIsExistCoeffCodeName } from '../../../common/CommonAPI';
import { isEmpty, size } from 'lodash';
import { defaultPartTypeData } from '../../../common/Constant';

const RadioGroup = Radio.Group;
dayjs.extend(customParseFormat);
const dateFormat = 'YYYY-MM-DD';

const CoefficientDetails = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    discardConfirmOk: discardConfirmOk,
    discardConfirmCancel: discardConfirmCancel,
    displayDetailInfo: displayDetailInfo,
    formValidation: formValidation,
  }));

  const editMode = props.editMode;

  const [id, setID] = useState('');
  const [display, setDisplay] = useState();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [coefficient, setCoefficient] = useState('');
  const [estimateDays, setEstimateDays] = useState('');
  const [remarks, setRemarks] = useState('');
  const [sortNo, setSortNo] = useState();
  const [created, setCreated] = useState('');
  const [updated, setUpdated] = useState('');
  const [tableNo, setTableNo] = useState('');
  // 管理係数個数
  const [kosuuMstData, setKosuuMstData] = useState([]);
  const [kosuuDataTemp, setKosuuDataTemp] = useState([]);
  const [kosuuList, setKosuuList] = useState([]);
  const [isKosuu, setIsKosuu] = useState(false);
  // 入力チェック
  const [errIndexVal, setErrIndexVal] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [updateConfirm, setUpdateConfirm] = useState(false);

  const [isNoData, setIsNoData] = useState(false);

  const inputItemRef = {};

  const inputRef = useRef([]);

  const [screenWidth, setScreenWidth] = useState('1000px');

  useEffect(() => {
    setScreenWidth(`${props.detailWidth - 10}px`);
  }, [props.detailWidth]);

  // 選択した行の情報を設定
  useEffect(() => {
    setScreenWidth(props.detailWidth ? `${props.detailWidth}px` : '1000px');
    let selectedRowData = props.selectedData;
    if (selectedRowData == null) {
      document.getElementById('edit').style.display = 'none';
      document.getElementById('update').style.display = 'none';
      document.getElementById('discard').style.display = 'none';
      setIsNoData(false);
    } else if (props.selectedData?.length === 0) {
      // 一覧の検索結果が無い場合、
      resetDetailInfo();
      setIsNoData(true);
      props.updateEditMode(false);
    } else if (tableNo !== props.tableNo || id !== selectedRowData?.id) {
      // 一覧の検索結果がある場合、
      displayDetailInfo(selectedRowData);
      setIsNoData(false);
      props.updateEditMode(false);
    }
    setTableNo(props.tableNo);
  }, [props.selectedData]);

  // 編集モードより、「更新、破壊、編集」ボタンの表示・非表示を判断する
  useEffect(() => {
    if (props.editMode) {
      document.getElementById('edit').style.display = 'none';
      document.getElementById('update').style.display = 'block';
      document.getElementById('discard').style.display = 'block';
    } else {
      document.getElementById('edit').style.display = 'block';
      document.getElementById('update').style.display = 'none';
      document.getElementById('discard').style.display = 'none';
    }
  }, [props.editMode]);

  // 個数ボタン押下
  const callKosuuMstData = () => {
    setErrIndexVal([]);
    setErrorMessage('');
    setIsKosuu(true);
  };

  // 個数マスタ一覧の「X」ボタン押下
  const closeAddModal = () => {
    if (isKosuu === true) {
      setIsKosuu(false);
    }
  };

  const addData = () => {
    setIsUpdateData(true);
  };

  // 個数マスタ一覧の値を入力された場合、
  const handleKosuuChange = (event, id, field, index) => {
    if (regexExp.test(event.target.value) && !isDigitZenkaku(event.target.value)) return;
    const temp = JSON.parse(JSON.stringify(kosuuDataTemp));
    const updatedData = temp?.map((row) => {
      if (row.no === id) {
        return { ...row, [field]: event.target.value };
      }
      return row;
    });

    setKosuuDataTemp(updatedData);
  };

  const isValueOrderCorrect = (e, no, field) => {
    const temp = kosuuDataTemp?.filter((i) => i.isOutOfRange === false);

    const insertIndex = temp?.findIndex((item) => item.no === no);
    if (regexExp.test(temp[insertIndex].value) && !isDigitZenkaku(temp[insertIndex].value)) return;
    // IQX_WEBEST-220の対応-START
    if (regexExp.test(temp[insertIndex].value)) {
      // 全角の場合、
      temp[insertIndex].value = toHalfWidth(temp[insertIndex].value); // 半角へ変換
      e.target.value = temp[insertIndex].value;
      handleKosuuChange(e, no, field);
    }
    if (isNaN(parseFloat(temp[insertIndex].value))) {
      setErrIndexVal([...errIndexVal, insertIndex]);
      setErrorMessage('数字値を入力してください！');
      return;
    }
    // IQX_WEBEST-220の対応-END

    if (insertIndex > 0) {
      const prevValue = parseFloat(temp[insertIndex - 1].value);
      const currentValue = parseFloat(temp[insertIndex].value);
      const nextValue = parseFloat(temp[insertIndex + 1].value);
      if (currentValue > prevValue && currentValue < nextValue) {
        let temp = [insertIndex - 1, insertIndex, insertIndex + 1];

        setErrIndexVal(errIndexVal?.filter((item) => item !== insertIndex && item != insertIndex - 1));

        setErrorMessage('前のしきい値より大きい値を入力してください！');
      } else {
        setErrIndexVal([...errIndexVal, insertIndex]);

        if (currentValue <= prevValue) {
          setErrorMessage('前のしきい値より大きい値を入力してください！');
        } else if (currentValue >= nextValue) {
          setErrorMessage('次のしきい値より小さい値を入力してください！');
        }
      }
    } else {
      const currentValue = parseFloat(temp[insertIndex].value);
      const nextValue = parseFloat(temp[insertIndex + 1].value);

      if (currentValue < nextValue) {
        setErrIndexVal(errIndexVal?.filter((item) => item !== insertIndex));
      } else {
        setErrIndexVal([...errIndexVal, insertIndex]);
        setErrorMessage('次のしきい値より小さい値を入力してください！');
      }
    }
  };

  // 個数マスタ一覧の追加ボタン押下
  const addKosuu = (no, index) => {
    inputRef.current[index].focus();
    const copyData = kosuuDataTemp.slice();

    const insertIndex = kosuuDataTemp?.findIndex((item) => item.no === no);

    if (insertIndex !== -1) {
      const data = {
        no: kosuuDataTemp?.length + 1,
        value: 0,
        isOutOfRange: false,
      };
      copyData.splice(insertIndex + 1, 0, data);
      setKosuuDataTemp(copyData);
    }
  };

  // 個数マスタ一覧の削除ボタン押下
  const deleteKosuu = (no, e, index) => {
    // 更新するエラーインデックスを得る
    const updatedErrorIndexes = errIndexVal?.filter((item) => item !== index);

    const finalErrorIndexes = updatedErrorIndexes?.map((item) => (item > index ? item - 1 : item));
    setErrIndexVal(finalErrorIndexes);

    const listTemp = JSON.parse(JSON.stringify(kosuuDataTemp));
    if (listTemp?.filter((item) => !item.isOutOfRange)?.length > 2) {
      const updatedData = listTemp?.map((row) => {
        if (row.no === no) {
          return { ...row, isOutOfRange: true };
        }
        return row;
      });

      setKosuuDataTemp(updatedData);
      const filterData = updatedData?.filter((i) => !i.isOutOfRange);
      if (index + 1 !== filterData?.length) {
        inputRef.current[index].focus();
      }
    }
  };

  // 個数マスタ一覧のCancelボタン押下
  const cancelMasterData = () => {
    setKosuuDataTemp(kosuuMstData);
    setIsKosuu(false);
  };

  // 個数マスタ一覧のOKボタン押下
  const okKosuuMasterData = () => {
    const deletedId = [];

    kosuuMstData?.forEach((item1, index) => {
      const found = kosuuDataTemp?.find((item2) => item2.no === item1.no && item2.isOutOfRange);
      if (found) {
        deletedId.push(item1.no);
      }
    });

    const newId = kosuuDataTemp
      ?.filter((item) => !item.isOutOfRange && !kosuuMstData.some((data2Item) => data2Item.no === item.no))
      ?.map((item) => item.no);

    const deleteSeisanKosuuList = deleteListColData(kosuuList, deletedId);

    const updateSeisanKosuuList = updateListColData(deleteSeisanKosuuList, newId);

    const sortSeisanKosuuList = sortListData(updateSeisanKosuuList, kosuuDataTemp);

    setIsKosuu(false);
    setKosuuDataTemp(kosuuDataTemp);
    setKosuuMstData(kosuuDataTemp);
    setKosuuList(sortSeisanKosuuList);
  };

  // 生産個数係数を入力された場合、
  const changeKosuu = (event, no) => {
    if (regexExp.test(event.target.value) && !isDigitZenkaku(event.target.value)) return;
    const newValue = event.target.value;
    setKosuuList((prevList) => prevList.map((item) => (item.no === no ? { ...item, value: newValue } : item)));
  };
  // 生産個数係数を’０’入力された場合、
  const changeKosuuOnBlur = (event, no) => {
    if (regexExp.test(event.target.value) && !isDigitZenkaku(event.target.value)) return;

    let eventVal = regexExp.test(event.target.value) ? toHalfWidth(event.target.value) : event.target.value;

    const newValue = parseFloat(eventVal) > 0 ? eventVal : 1;

    setKosuuList((prevList) => prevList.map((item) => (item.no === no ? { ...item, value: newValue } : item)));
  };

  // 個数マスタ一覧のヘッダー
  const addKosuuModalTitle = (
    <div
      style={{
        width: 400,
        backgroundColor: '#005fab',
        marginLeft: -24,
        height: 36,
        marginTop: -20,
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
        fontSize: 14,
      }}
    >
      <p style={{ paddingTop: 8, paddingLeft: 18, color: 'white' }}>個数</p>
    </div>
  );

  // 個数マスタ一覧
  const addKosuuContent = (
    <>
      <div style={{ height: 400, overflow: 'auto' }}>
        <Row className="chargeAmount registerModal">
          <Col style={{ width: '100%' }}>
            <Table className="master-table">
              <thead>
                <tr style={{ textAlign: 'center' }}>
                  <th style={{ width: '20%' }} className="param-tbl-header">
                    No
                  </th>

                  <th style={{ width: '60%' }} className="param-tbl-header">
                    <Space>
                      <label>値</label>
                    </Space>
                  </th>
                </tr>
              </thead>
              <tbody>
                {kosuuDataTemp
                  ?.filter((subitem) => !subitem.isOutOfRange)
                  ?.map((item, index) => (
                    <tr key={index} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <td>{index + 1}</td>

                      <td>
                        {errIndexVal?.includes(index) ? (
                          <Popover
                            content={<div style={{ padding: 7 }}>{errorMessage}</div>}
                            placement="left"
                            visible={true}
                          ></Popover>
                        ) : (
                          ''
                        )}
                        {/* {errIndexVal} */}
                        <Input
                          value={item.value}
                          onChange={(e) => handleKosuuChange(e, item.no, 'value')}
                          onBlur={(e) => isValueOrderCorrect(e, item.no, 'value')}
                          onPressEnter={(e) => isValueOrderCorrect(e, item.no, 'value')}
                          className={errIndexVal?.includes(index) ? 'red-border' : ''}
                          style={{
                            ...getInputBorderStyle(index, kosuuDataTemp),
                            pointerEvents: editMode ? 'auto' : 'none',
                          }}
                          id={'kosuu' + index + 1}
                          ref={(ref) => (inputRef.current[index] = ref)}
                          onKeyPress={(e) => {
                            handleKeyPress(e, false);
                          }}
                        ></Input>
                      </td>
                      <td
                        style={{ border: 'none', backgroundColor: 'white', verticalAlign: 'middle', width: '20%' }}
                        className="min"
                      >
                        <Row justify="center">
                          <PlusCircleFilled
                            onClick={() => addKosuu(item.no, index + 1)}
                            className="add-remove-icon"
                            style={{ marginLeft: 5, marginRight: 5, pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                          <CloseCircleFilled
                            className="add-remove-icon"
                            onClick={(e) => deleteKosuu(item.no, e, index)}
                            style={{ pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                        </Row>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>
      <Row justify="center" className="mt-2">
        <Button
          className={isEmpty(errIndexVal) ? 'mainButton' : 'disabledButton'}
          id="update"
          onClick={okKosuuMasterData}
        >
          OK
        </Button>
        <Button className="cancelButton" style={{ marginLeft: 10 }} onClick={cancelMasterData}>
          キャンセル
        </Button>
      </Row>
    </>
  );

  // 詳細情報をリセットする。
  const resetDetailInfo = () => {
    setID('');
    setDisplay('');
    setName('');
    setCoefficient('');
    setEstimateDays('');
    setCode('');
    setRemarks('');
    setSortNo('');
    setCreated('');
    setUpdated('');
    setTableNo('');
  };

  // 詳細情報を表示する
  const displayDetailInfo = (data) => {
    setID(data.id);
    setDisplay(data.isUsed);
    setName(data.name);
    setCoefficient(toDecimal(data.coeff, 2));
    setEstimateDays(data.estimateDays);
    setCode(data.code);
    setRemarks(data.remarks);
    setSortNo(data.sortNum);
    setCreated(data.created?.split('T')[0]);
    setUpdated(data.modified?.split('T')[0]);
    /* setTableNo(data.tableNo); */
    // setKosuuMstData(data.managementCoeffThMaster);
    // setKosuuList(data.managementCoeffThItems);
    // 2025-05-28 共通パラメータの係数を新規作成について。納入品係数と管理係数が8個までしか作成できない。そういう仕様でしょうか？
    // 旧データの場合は管理係数のdata.managementCoeffThMasterとdata.managementCoeffThItemsがないので、
    // デフォルト値表示される。デフォルト値を表示の場合、
    // managementCoeffThItemsの[0]のデータを旧データのcoeffデータと合わせて表示する
    let updatedItems = [];

    if (props?.tableNo === 4) {
      if (data.managementCoeffThItems !== undefined && data.managementCoeffThItems.length > 0) {
        updatedItems = [...data.managementCoeffThItems];
        if (data.coeff !== updatedItems[0]?.value) {
          updatedItems[0] = {
            ...updatedItems[0],
            value: data.coeff,
          };
        }
      } else {
        updatedItems = data.managementCoeffThItems || [];
      }
      setKosuuMstData(data.managementCoeffThMaster);
      setKosuuList(updatedItems);
      if (data.managementCoeffThMaster && [...data.managementCoeffThMaster]?.length > 0) {
        setKosuuDataTemp(data.managementCoeffThMaster);
      } else {
        setKosuuDataTemp(defaultQuantiyData);
      }
    }
    // 段取係数、子部品生産個数
    if (props?.tableNo === 9) {
      if (data.setupCoeffThItems !== undefined && data.setupCoeffThItems.length > 0) {
        updatedItems = [...data.setupCoeffThItems];
        if (data.coeff !== updatedItems[0]?.value) {
          updatedItems[0] = {
            ...updatedItems[0],
            value: data.coeff,
          };
        }
      } else {
        updatedItems = data.setupCoeffThItems || [];
      }
      setKosuuMstData(data.setupCoeffThMaster);
      setKosuuList(updatedItems);
      if (data.setupCoeffThMaster && [...data.setupCoeffThMaster]?.length > 0) {
        setKosuuDataTemp(data.setupCoeffThMaster);
      } else {
        setKosuuDataTemp(defaultPartTypeData);
      }
    }
    if (props?.tableNo === 10) {
      if (data.productCoeffThItems !== undefined && data.productCoeffThItems.length > 0) {
        updatedItems = [...data.productCoeffThItems];
        if (data.coeff !== updatedItems[0]?.value) {
          updatedItems[0] = {
            ...updatedItems[0],
            value: data.coeff,
          };
        }
      } else {
        updatedItems = data.productCoeffThItems || [];
      }
      setKosuuMstData(data.productCoeffThMaster);
      setKosuuList(updatedItems);
      if (data.productCoeffThMaster && [...data.productCoeffThMaster]?.length > 0) {
        setKosuuDataTemp(data.productCoeffThMaster);
      } else {
        setKosuuDataTemp(defaultQuantiyData);
      }
    }
  };

  // 編集ボタンの押下
  const editData = () => {
    props.updateEditMode(true);
  };

  // 更新ボタンの押下
  const updateData = () => {
    // setUpdateConfirm(true);
    setUpdateConfirm(props.showEdtConfirmOK);
    if (!props.showEdtConfirmOK) {
      updConfirmOk();
    }
  };

  // 破壊ボタンの押下
  const discardData = () => {
    //setCancelConfirm(true);
    setCancelConfirm(props.showEdtConfirmCancle);
    if (!props.showEdtConfirmCancle) {
      discardConfirmOk();
    }
  };

  // 更新確認ポップアップのOKボタン押下
  const updConfirmOk = async (e) => {
    setUpdateConfirm(false);
    // 画面の入力チェック
    if (!(await formValidation(inputItemRef, formValidatorMode.Edit, tableNo))) {
      return;
    }
    props.updateEditMode(false);
    submitData();
  };

  // 更新確認ポップアップのCancelボタン押下
  const updConfirmCancel = () => {
    setUpdateConfirm(false);
  };

  // 破壊確認ポップアップのOKボタン押下
  const discardConfirmOk = () => {
    props.updateEditMode(false);
    let data = props.selectedData;
    displayDetailInfo(data);
    setCancelConfirm(false);
  };

  // 破壊確認ポップアップのCancelボタン押下
  const discardConfirmCancel = () => {
    setCancelConfirm(false);
  };

  // 入力された情報を更新する
  const submitData = () => {
    if (props.selectedData && props.selectedData.id === id) {
      props.selectedData.isUsed = display;
      props.selectedData.name = name;
      // props.selectedData.coeff = parseFloat(coefficient);
      // props.selectedData.coeff = coefficient;
      props.selectedData.estimateDays = parseInt(estimateDays);
      props.selectedData.code = code;
      props.selectedData.remarks = remarks;
      props.selectedData.sortNum = sortNo;
      // props.selectedData.created = created;
      // props.selectedData.modified = today;
      // props.selectedData.tableNo = tableNo;
      if (tableNo === 4) {
        props.selectedData.managementCoeffThMaster = kosuuMstData;
        props.selectedData.managementCoeffThItems = kosuuList;
        props.selectedData.coeff = kosuuList.length > 0 ? kosuuList[0].value : coefficient;
      } else if (tableNo === 9) {
        props.selectedData.setupCoeffThMaster = kosuuMstData;
        props.selectedData.setupCoeffThItems = kosuuList;
        props.selectedData.coeff = kosuuList.length > 0 ? kosuuList[0].value : coefficient;
      } else if (tableNo === 10) {
        props.selectedData.productCoeffThMaster = kosuuMstData;
        props.selectedData.productCoeffThItems = kosuuList;
        props.selectedData.coeff = kosuuList.length > 0 ? kosuuList[0].value : coefficient;
      } else {
        props.selectedData.coeff = coefficient;
      }

      props.submitData(props.selectedData, tableNo);
    }
  };

  // 画面の入力チェック
  const formValidation = async (inputItems, procMode, tableNo) => {
    let ret = true;
    let message = [];
    // コード
    let input = inputItems['code']?.input;
    input?.classList?.remove('input-error');
    if (input?.value?.length <= 0 && procMode !== formValidatorMode.BulkEdit) {
      // 必須
      message.push(<li>{ErrorMessage('コード').E007}</li>);
      input.classList.add('input-error');
      ret = false;
    }

    // 名称
    input = inputItems['name']?.input;
    input?.classList?.remove('input-error');
    if (input?.value?.length <= 0 && procMode !== formValidatorMode.BulkEdit) {
      // 必須
      message.push(<li>{ErrorMessage('名称').E007}</li>);
      input.classList.add('input-error');
      ret = false;
    }

    let coeffId = procMode === formValidatorMode.Create ? 0 : id;
    let code = inputItems['code']?.input?.value;
    let name = inputItems['name']?.input?.value;
    if (code !== '' && name !== '') {
      let isExistCodeName = await getIsExistCoeffCodeName(coeffId, code, name, tableNo);
      if (isExistCodeName) {
        message.push(<li>{ErrorMessage('コードと名前').E020}</li>);
        input = inputItems['code']?.input;
        input?.classList?.remove('input-error');
        input?.classList?.add('input-error');
        input = inputItems['name']?.input;
        input?.classList.add('input-error');
        ret = false;
      }
    }

    // 係数
    input = inputItems['coefficient']?.input;
    input?.classList?.remove('input-error');

    if (input?.value?.length <= 0) {
      // 必須
      message.push(<li>{ErrorMessage('係数').E007}</li>);
      input.classList.add('input-error');
      ret = false;
    }

    if (input?.value?.length > 0 && !isDecimal(input?.value)) {
      message.push(<li>{ErrorMessage('係数').E009}</li>);
      input.classList.add('input-error');
      ret = false;
    }

    // ソート番号
    input = inputItems['sortNo']?.input;
    input?.classList?.remove('input-error');
    if (input?.value?.length > 0 && !isDigit(input?.value)) {
      message.push(<li>{ErrorMessage('ソート番号').E009}</li>);
      input.classList.add('input-error');
      ret = false;
    }

    if (message?.length > 0) error(<ul>{message}</ul>);

    return ret;
  };

  return (
    <>
      <Form>
        {/* ボタン部分 */}
        <div className="d-flex" style={{ marginTop: 9, marginRight: 10 }}>
          <div>
            <Button className="mainButton" style={{ marginLeft: 10, display: 'none' }} id="update" onClick={updateData}>
              更新
            </Button>
          </div>
          <div>
            <Button
              className="cancelButton"
              style={{ marginLeft: 10, display: 'none' }}
              id="discard"
              onClick={discardData}
            >
              {/* 2025-05-14 パラメータの編集を押下した際、「更新」と「破棄」のボタンがあるが、
              編集をやめて戻りたいときに破棄の表示だと登録されている情報を破棄するように感じてしまうため、
              破棄の表記ではなく「戻る」や「終了」にするのはどうか */}
              {/* 破棄 */}
              戻る
            </Button>
          </div>
          <div className="ms-auto">
            <Button className={isNoData ? 'disabledButton' : 'mainButton'} id="edit" onClick={editData}>
              編集
            </Button>
          </div>
        </div>
        {/* プロパティ表示 */}
        <div className="overflow-auto" style={{ height: 760, marginTop: 10, overflowX: 'hidden' }}>
          <div id="showData" style={{ overflowX: 'hidden' }}>
            <Table id="param_detail_tbl" className="propertiesTbl">
              <thead>
                {/* コード */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">コード</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Input
                      className={editMode ? 'input-editable' : 'input-non-editable'}
                      name="code"
                      id="code"
                      value={code}
                      type="text"
                      onChange={(e) => setCode(e.target.value)}
                      ref={(ref) => {
                        inputItemRef['code'] = ref;
                      }}
                    />
                  </td>
                </tr>
                {/* 名称 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">名称</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Input
                      className={editMode ? 'input-editable' : 'input-non-editable'}
                      name="name"
                      id="name"
                      value={name}
                      type="text"
                      onChange={(e) => setName(e.target.value)}
                      ref={(ref) => {
                        inputItemRef['name'] = ref;
                      }}
                    />
                  </td>
                </tr>
                {/* 係数 */}
                {(Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 4) ||
                (Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 9) ||
                (Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 10) ? (
                  <tr className="detail-tbl-row">
                    <td className="param-detail-tbl-val-title">
                      <label className="param_detail-title-label">係数</label>
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <Input
                        className="input-non-editable"
                        name="coefficient"
                        id="coefficient"
                        value={kosuuList?.[0]?.value}
                        type="text"
                      />
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr className="detail-tbl-row">
                      <td className="param-detail-tbl-val-title">
                        <label className="param_detail-title-label">係数</label>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <Input
                          className={editMode ? 'input-editable' : 'input-non-editable'}
                          name="coefficient"
                          id="coefficient"
                          // value={coefficient}
                          value={coefficient}
                          type="text"
                          onBlur={(e) => setCoefficient(toDecimal(props?.onChangeInputForm(e.target.value), 2))}
                          onPressEnter={(e) => setCoefficient(toDecimal(props?.onChangeInputForm(e.target.value), 2))}
                          onChange={(e) => setCoefficient(props?.onChangeInputForm(e.target.value, false))}
                          onKeyPress={(e) => handleKeyPress(e, false)}
                          ref={(ref) => {
                            inputItemRef['coefficient'] = ref;
                          }}
                        />
                      </td>
                    </tr>
                  </>
                )}
                {/* 管理費係数の詳細 */}
                {Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 4 ? (
                  <tr>
                    <td colSpan={2}>
                      <div
                        className="table-container"
                        style={{
                          margin: 5,
                          width: screenWidth,
                          overflowX: 'auto',
                        }}
                      >
                        <Table
                          className="management"
                          style={{
                            width: '100%',
                          }}
                        >
                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '98px' }} className="param-tbl-header fixedCol">
                              <Row justify="center" className="ant-row-no-bg-color">
                                {editMode ? (
                                  <>
                                    <Button
                                      className="cancleButton"
                                      onClick={callKosuuMstData}
                                      style={{ pointerEvents: 'auto' }}
                                    >
                                      個数
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <label>個数</label>
                                  </>
                                )}
                              </Row>
                            </td>

                            {kosuuMstData
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((item, index) => (
                                <td key={index} style={{ width: '98px' }} className="param-tbl-header">
                                  ～{item.value}個
                                </td>
                              ))}
                          </tr>

                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '98px' }} className="fixedOneRowCol">
                              {'管理費係数'}
                            </td>
                            {kosuuList
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((row, index) => (
                                <td key={index} style={{ width: '98px' }}>
                                  <Input
                                    value={editMode ? row.value : toDecimal(row.value, 2)}
                                    onChange={(e) => changeKosuu(e, row.no)}
                                    onBlur={(e) => changeKosuuOnBlur(e, row.no)}
                                    onPressEnter={(e) => changeKosuuOnBlur(e, row.no)}
                                    onKeyPress={(e) => {
                                      handleKeyPress(e, false);
                                    }}
                                    style={{ padding: 3, width: '90.2%', height: 26, textAlign: 'center' }}
                                    className={editMode ? 'input-editable ' : 'input-non-editable'}
                                  />
                                </td>
                              ))}
                          </tr>
                        </Table>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {/* 段取係数の詳細 */}
                {Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 9 ? (
                  <tr>
                    <td colSpan={2}>
                      <div
                        className="table-container"
                        style={{
                          margin: 5,
                          width: screenWidth,
                          overflowX: 'auto',
                        }}
                      >
                        <Table
                          className="management"
                          style={{
                            width: '100%',
                          }}
                        >
                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '98px' }} className="param-tbl-header fixedCol">
                              <Row justify="center" className="ant-row-no-bg-color">
                                {editMode ? (
                                  <>
                                    <Button
                                      className="cancleButton"
                                      onClick={callKosuuMstData}
                                      style={{ pointerEvents: 'auto' }}
                                    >
                                      種類
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <label>種類</label>
                                  </>
                                )}
                              </Row>
                            </td>

                            {kosuuMstData
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((item, index) => (
                                <td key={index} style={{ width: '98px' }} className="param-tbl-header">
                                  ～{item.value}種類
                                </td>
                              ))}
                          </tr>

                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '98px' }} className="fixedOneRowCol">
                              {'段取り係数'}
                            </td>
                            {kosuuList
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((row, index) => (
                                <td key={index} style={{ width: '98px' }}>
                                  <Input
                                    value={editMode ? row.value : toDecimal(row.value, 2)}
                                    onChange={(e) => changeKosuu(e, row.no)}
                                    onBlur={(e) => changeKosuuOnBlur(e, row.no)}
                                    onPressEnter={(e) => changeKosuuOnBlur(e, row.no)}
                                    onKeyPress={(e) => {
                                      handleKeyPress(e, false);
                                    }}
                                    style={{ padding: 3, width: '90.2%', height: 26, textAlign: 'center' }}
                                    className={editMode ? 'input-editable ' : 'input-non-editable'}
                                  />
                                </td>
                              ))}
                          </tr>
                        </Table>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {/* 子部品生産個数係数の詳細 */}
                {Array.isArray(kosuuMstData) && kosuuMstData.length > 0 && props?.tableNo === 10 ? (
                  <tr>
                    <td colSpan={2}>
                      <div
                        className="table-container"
                        style={{
                          margin: 5,
                          width: screenWidth,
                          overflowX: 'auto',
                        }}
                      >
                        <Table
                          className="management"
                          style={{
                            width: '100%',
                          }}
                        >
                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '128px' }} className="param-tbl-header fixedCol">
                              <Row justify="center" className="ant-row-no-bg-color">
                                {editMode ? (
                                  <>
                                    <Button
                                      className="cancleButton"
                                      onClick={callKosuuMstData}
                                      style={{ pointerEvents: 'auto' }}
                                    >
                                      製造数
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <label>製造数</label>
                                  </>
                                )}
                              </Row>
                            </td>

                            {kosuuMstData
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((item, index) => (
                                <td key={index} style={{ width: '98px' }} className="param-tbl-header">
                                  ～{item.value}個
                                </td>
                              ))}
                          </tr>

                          <tr style={{ textAlign: 'center' }}>
                            <td style={{ width: '128px' }} className="fixedOneRowCol">
                              {'子部品生産個数係数'}
                            </td>
                            {kosuuList
                              ?.filter((subitem) => !subitem.isOutOfRange)
                              ?.map((row, index) => (
                                <td key={index} style={{ width: '98px' }}>
                                  <Input
                                    value={editMode ? row.value : toDecimal(row.value, 2)}
                                    onChange={(e) => changeKosuu(e, row.no)}
                                    onBlur={(e) => changeKosuuOnBlur(e, row.no)}
                                    onPressEnter={(e) => changeKosuuOnBlur(e, row.no)}
                                    onKeyPress={(e) => {
                                      handleKeyPress(e, false);
                                    }}
                                    style={{ padding: 3, width: '90.2%', height: 26, textAlign: 'center' }}
                                    className={editMode ? 'input-editable ' : 'input-non-editable'}
                                  />
                                </td>
                              ))}
                          </tr>
                        </Table>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {/* 目安日数 */}
                {estimateDays ? (
                  <tr className="detail-tbl-row">
                    <td className="param-detail-tbl-val-title">
                      <label className="param_detail-title-label">目安日数</label>
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <Input
                        className={editMode ? 'input-editable' : 'input-non-editable'}
                        name="estimateDays"
                        id="estimateDays"
                        value={estimateDays}
                        type="text"
                        onChange={(e) => setEstimateDays(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, true)}
                        ref={(ref) => {
                          inputItemRef['estimateDays'] = ref;
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  <></>
                )}

                {/* ソート番号 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">ソート番号</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <Input
                      className={editMode ? 'input-editable' : 'input-non-editable'}
                      name="sortNo"
                      id="sortNo"
                      value={sortNo}
                      type="text"
                      onBlur={(e) => setSortNo(props?.onChangeInputForm(e.target.value))}
                      onPressEnter={(e) => setSortNo(props?.onChangeInputForm(e.target.value))}
                      onChange={(e) => setSortNo(props?.onChangeInputForm(e.target.value, false))}
                      onKeyPress={(e) => handleKeyPress(e, true)}
                      ref={(ref) => {
                        inputItemRef['sortNo'] = ref;
                      }}
                    />
                  </td>
                </tr>
                {/* 作成日 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">作成日</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <label className="param_detail-title-label">{created}</label>
                  </td>
                </tr>
                {/* 更新日 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">更新日</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <label className="param_detail-title-label">{updated}</label>
                  </td>
                </tr>
                {/* 備考 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">備考</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    <TextArea
                      className={editMode ? 'input-editable' : 'input-non-editable'}
                      name="remarks"
                      id="remarks"
                      value={remarks}
                      style={{ margin: 5, padding: 5, width: '98.3%' }}
                      type="text"
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </td>
                </tr>

                {/* 表示 */}
                <tr className="detail-tbl-row">
                  <td className="param-detail-tbl-val-title">
                    <label className="param_detail-title-label">表示</label>
                  </td>
                  <td style={{ verticalAlign: 'middle' }}>
                    {editMode ? (
                      <RadioGroup
                        name="isUsed"
                        defaultValue={display}
                        className="radioCustomerGpEdit"
                        onChange={(e) => setDisplay(e.target.value)}
                        value={display}
                      >
                        <Radio value={true}>オン</Radio>
                        <Radio value={false}>オフ</Radio>
                      </RadioGroup>
                    ) : display ? (
                      <Image preview={false} width={13} src={checked} id="imgOutsourcingFg" />
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              </thead>
            </Table>
          </div>
        </div>
      </Form>
      {/* 更新確認_Modal */}
      {updateConfirm
        ? commonResponsiveConfirmModal(
            updateConfirm,
            confirmModalTitle,
            commonFooter(updConfirmOk, updConfirmCancel),
            null,
            400,
            updConfirmOk,
            updConfirmCancel,
            confirmUpdateContent,
            null,
            null,
            false
          )
        : ''}
      {/* 破棄確認_Modal */}
      {cancelConfirm
        ? commonResponsiveConfirmModal(
            cancelConfirm,
            confirmModalTitle,
            commonFooter(discardConfirmOk, discardConfirmCancel),
            null,
            400,
            discardConfirmOk,
            discardConfirmCancel,
            confirmDiscardContent,
            null,
            null,
            false
          )
        : ''}
      {/* 個数 */}
      {isKosuu
        ? commonModal(
            isKosuu,
            addKosuuModalTitle,
            null,
            null,
            400,
            addData,
            closeAddModal,
            addKosuuContent,
            null,
            null,
            true
          )
        : ''}
    </>
  );
});

export default CoefficientDetails;
